import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { StatusPill } from "./ShopDashboard";
import { Truck, Phone, MapPin } from "@phosphor-icons/react";

const NEXT_STATUS = {
  new: { label: "Accept", next: "accepted" },
  accepted: { label: "Mark Packed", next: "packed" },
  packed: { label: "Out for delivery", next: "out_for_delivery" },
  out_for_delivery: { label: "Delivered", next: "delivered" },
  out_for_self_delivery: { label: "Delivered", next: "delivered" },
};

export default function ShopOrders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const r = await api.get("/orders/my");
    setOrders(r.data);
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status, deliveryMode) => {
    try {
      await api.patch(`/orders/${id}/status`, { status, delivery_mode: deliveryMode });
      toast.success("Order updated");
      load();
    } catch { toast.error("Failed"); }
  };

  const filtered = filter === "all" ? orders : orders.filter((o) => o.status === filter);
  const statuses = ["all", "new", "accepted", "packed", "out_for_delivery", "delivered", "cancelled"];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight">Orders</h1>
        <p className="text-gray-600 text-sm">{orders.length} total</p>
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
        {statuses.map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            data-testid={`orders-filter-${s}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold capitalize whitespace-nowrap ${
              filter === s ? "bg-black text-white" : "bg-white border border-gray-200 text-gray-600"
            }`}>
            {s.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.map((o) => (
          <div key={o.id} className="qd-card p-4" data-testid={`order-card-${o.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="font-mono text-xs text-gray-500">{o.order_number}</div>
                <div className="font-heading font-bold text-lg">{o.customer_name}</div>
                <div className="text-xs text-gray-500 flex items-center gap-1 mt-1"><Phone size={12} /> {o.customer_mobile}</div>
              </div>
              <div className="text-right">
                <StatusPill status={o.status} />
                <div className="font-heading font-black text-xl mt-1">₹{o.total}</div>
                <div className="text-xs text-gray-500 uppercase">{o.payment_mode}</div>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <div className="bg-gray-50 rounded-lg p-2">
                <div className="text-gray-500">Items</div>
                <div className="font-bold">{o.items.length} item{o.items.length > 1 && "s"}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-2">
                <div className="text-gray-500">ETA</div>
                <div className="font-bold">{o.eta_minutes} min</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-2">
                <div className="text-gray-500 flex items-center gap-1"><MapPin size={12} /> Pincode</div>
                <div className="font-bold">{o.pincode}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-2">
                <div className="text-gray-500 flex items-center gap-1"><Truck size={12} /> Mode</div>
                <div className="font-bold capitalize">{o.delivery_mode}</div>
              </div>
            </div>

            <div className="mt-3 text-xs text-gray-600">
              <b>Items:</b> {o.items.map((i) => `${i.name} x${i.qty}`).join(", ")}
            </div>
            <div className="mt-1 text-xs text-gray-600">
              <b>Address:</b> {o.address.address}, {o.address.pincode}
            </div>

            {NEXT_STATUS[o.status] && (
              <div className="mt-3 flex flex-wrap items-center gap-2 pt-3 border-t border-gray-100">
                {o.status === "accepted" && (
                  <select
                    onChange={(e) => updateStatus(o.id, "packed", e.target.value)}
                    defaultValue={o.delivery_mode}
                    className="qd-input !py-2 !px-3 !w-auto text-xs"
                    data-testid={`order-delivery-mode-${o.id}`}>
                    <option value="self">Self delivery</option>
                    <option value="borzo">Borzo</option>
                    <option value="porter">Porter</option>
                    <option value="shadowfax">Shadowfax</option>
                  </select>
                )}
                <button onClick={() => updateStatus(o.id, NEXT_STATUS[o.status].next)}
                  className="qd-btn-green text-xs !py-2 !px-4"
                  data-testid={`order-advance-${o.id}`}>
                  {NEXT_STATUS[o.status].label} →
                </button>
                {o.status === "new" && (
                  <button onClick={() => updateStatus(o.id, "cancelled")}
                    className="text-xs px-3 py-2 rounded-full border border-red-200 text-red-600 font-bold"
                    data-testid={`order-reject-${o.id}`}>
                    Reject
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
        {filtered.length === 0 && <div className="text-center py-12 text-gray-500">No orders</div>}
      </div>
    </div>
  );
}
